import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

import { CACHE_LIFETIME, indexedDBService } from '../indexedDBService';
import { revalidationService } from '../revalidationService';

// Common interfaces
export interface BaseQueryOptions<T, R = never> {
  cacheKey: string;
  transform?: undefined extends R ? never : (data: T) => R;
  skip?: boolean;
  revalidate?: boolean;
  cacheLifetime?: number;
}

export interface QueryResult<T, R> {
  data?: T | R | null;
  isLoading?: boolean;
  error?: any;
}

export type TransformedType<T, R> = undefined extends R ? T : R;

export function useIndexedDBBase<T, R>({
  cacheKey,
  skip = false,
  cacheLifetime = CACHE_LIFETIME,
}: Omit<BaseQueryOptions<T, R>, 'revalidate'>) {
  const [localData, setLocalData] = useState<T | null>(null);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadCachedData = async () => {
      if (skip) {
        setIsLoadingCache(false);
        return;
      }

      try {
        const cached = await indexedDBService.get(cacheKey);

        if (cached?.data && mounted) {
          // Always display cached data first for a better UX.
          setLocalData(cached.data as T);

          const age = Date.now() - cached.timestamp;
          if (
            age < cacheLifetime
            && !revalidationService.shouldRevalidate(cacheKey, cached.timestamp)
          ) {
            // Data is fresh, no need to refetch.
            setShouldFetch(false);
          } else {
            // Data is stale or needs revalidation, fetch in the background.
            setShouldFetch(true);
          }
        } else if (mounted) {
          // No data in cache, must fetch.
          setShouldFetch(true);
        }
      } catch (error) {
        Sentry.captureMessage(`Failed to load cached data for ${cacheKey}: ${error}`);
        if (mounted) {
          setShouldFetch(true);
        }
      } finally {
        if (mounted) {
          setIsLoadingCache(false);
        }
      }
    };

    loadCachedData();

    return () => {
      mounted = false;
    };
  }, [cacheKey, skip, cacheLifetime]);

  return {
    localData,
    isLoadingCache,
    shouldFetch,
    setLocalData,
  };
}

// Helper function to process data
export function processQueryData<T, R>(
  apiData: T | undefined,
  localData: T | null,
  transform?: (data: T) => R,
): TransformedType<T, R> {
  const finalData = apiData || localData;
  return (finalData && transform ? transform(finalData) : finalData) as TransformedType<T, R>;
}
